import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";

/** The three ways to feed a Profile Tracker (PREC-3417). */
export enum TrackerSourceMode {
	PEOPLE = "PEOPLE",
	COMPANIES = "COMPANIES",
	IMPORT = "IMPORT",
}

interface TrackerSourceModeSwitchProps {
	mode: TrackerSourceMode;
	onChange: (mode: TrackerSourceMode) => void;
}

const MODES: { mode: TrackerSourceMode; labelKey: string }[] = [
	{ mode: TrackerSourceMode.PEOPLE, labelKey: "people" },
	{ mode: TrackerSourceMode.COMPANIES, labelKey: "companies" },
	{ mode: TrackerSourceMode.IMPORT, labelKey: "import" },
];

/**
 * Choosing what kind of source to add. Deliberately three equal buttons rather than a
 * dropdown: the choice decides which identifier is required, so it has to be visible while
 * the user fills the form in, not hidden behind a closed control.
 */
export const TrackerSourceModeSwitch: FunctionComponent<TrackerSourceModeSwitchProps> = ({
	mode,
	onChange,
}) => {
	const { t } = useTranslation();

	return (
		<div className="flex gap-2">
			{MODES.map((option) => (
				<button
					key={option.mode}
					className={classNames(
						"flex flex-1 min-w-0 cursor-pointer items-center justify-center gap-1.5 truncate",
						"rounded-[10px] border px-2 py-2.5 text-xs font-semibold duration-200",
						mode === option.mode
							? "border-brand-light bg-brand-light text-white"
							: "border-gray-200 bg-white text-text hover:border-brand-light hover:text-brand-light"
					)}
					data-testid={`tracker-source-mode-${option.labelKey}`}
					type="button"
					onClick={() => onChange(option.mode)}
				>
					{t(`profileTracker.sources.mode.${option.labelKey}`)}
				</button>
			))}
		</div>
	);
};
